import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DOCUMENT,
    ElementRef,
    inject,
    OnDestroy,
    Renderer2,
    signal,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReverseThemeDirective } from '@interacta-shared/data-access-theme';
import { leaveWith, ModalSurfaceService } from '@interacta-shared/ui';
import { deltaToPlainText } from '@interacta-shared/util-common';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, map, switchMap, tap, timer } from 'rxjs';
import {
    TipActionCallback,
    TipData,
    TipSeverity,
} from '../../models/tip.model';
import { isProTip, isSimpleTip, stripHtml } from '../../models/tip.utils';
import { closeCurrentTip } from '../../store/tip.actions';
import { selectCurrentTip } from '../../store/tip.selectors';
import { ProTipComponent } from '../pro-tip/pro-tip.component';
import { SimpleTipComponent } from '../simple-tip/simple-tip.component';

const ANNOUNCEMENT_ATTRIBUTE = 'data-tip-announcement';
const ANNOUNCEMENT_DELAY_MS = 100;

/**
 * Tip wrapper for animation and container graphic only
 * @see Figma https://www.figma.com/design/wjk2yFscQ7BG1pI3TZ82sq/Lapidaris-style-guide?node-id=27006-68080&t=mQVPRB1nNDVEJ515-4
 * for better graphic examples
 */
@Component({
    imports: [SimpleTipComponent, ProTipComponent, ReverseThemeDirective],
    selector: 'interacta-tip-overlay',
    templateUrl: './tip-overlay.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverlayTipsComponent<A, P> implements OnDestroy {
    private readonly store = inject(Store);
    private readonly overlay = inject(Overlay);
    private readonly vcr = inject(ViewContainerRef);
    private readonly translateService = inject(TranslateService);
    private readonly modalSurfaceService = inject(ModalSurfaceService);
    private readonly renderer = inject(Renderer2);
    private readonly document = inject(DOCUMENT);

    private overlayRef: OverlayRef | null = null;

    private readonly currentTip = this.store.selectSignal(selectCurrentTip);

    /**
     * Tip actually rendered in the template. It lags {@link currentTip} so a new
     * tip only enters once the previous one has finished its leave animation,
     * avoiding two tips coexisting in the overlay during navigation.
     */
    protected readonly renderedTip = signal<TipData | null>(null);
    private pendingTip: TipData | null = null;
    private isLeaving = false;

    private readonly tipContent =
        viewChild.required<TemplateRef<unknown>>('tipContent');
    private readonly rootAnnouncementRegion =
        viewChild<ElementRef<HTMLElement>>('announcer');

    constructor() {
        toObservable(this.currentTip)
            .pipe(takeUntilDestroyed())
            .subscribe((tip) => this.syncRenderedTip(tip));

        // An open modal hides the root live region from assistive technology,
        // so the modal gets a region of its own. It is created when the modal
        // opens: a region inserted together with its text is not announced.
        this.modalSurfaceService
            .topModal$()
            .pipe(takeUntilDestroyed())
            .subscribe((modal) => {
                if (modal != null) {
                    this.modalAnnouncementRegion(modal);
                }
            });

        // Every region is emptied at once and the text lands a moment later in
        // the one exposed then: the top modal's, or the root one. Screen readers
        // skip a change that leaves the text as it was, VoiceOver misses one made
        // in the same task as the clear, and the top modal can change in between.
        // Driven by the rendered tip, not the text: a tip replaced by another
        // with the same message is announced again.
        toObservable(this.renderedTip)
            .pipe(
                map(() => this.announcement()),
                tap(() => this.clearAnnouncementRegions()),
                switchMap((text) =>
                    text.length > 0
                        ? timer(ANNOUNCEMENT_DELAY_MS).pipe(map(() => text))
                        : EMPTY,
                ),
                takeUntilDestroyed(),
            )
            .subscribe((text) => this.writeAnnouncement(text));
    }

    readonly simpleTip = computed(() =>
        isSimpleTip(this.renderedTip()) ? this.renderedTip() : null,
    );

    readonly proTip = computed(() => {
        const current = this.renderedTip();
        return isProTip<A, P>(current) ? current : null;
    });

    readonly severity = computed(() => {
        const tip = this.renderedTip();
        return tip == null ? null : TipSeverity[tip.severity].toLowerCase();
    });

    private readonly announcement = computed(() => {
        const tip = this.renderedTip();
        if (tip == null) {
            return '';
        }

        const title = isProTip(tip) && tip.title ? tip.title : null;
        // Callers forward backend payloads as-is: message can be null at runtime.
        const rawMessage = tip.message ?? '';
        const message =
            typeof rawMessage === 'string'
                ? this.translate(rawMessage, tip.translateParams)
                : deltaToPlainText(rawMessage);

        return [
            title ? this.translate(title, tip.translateParams) : '',
            message,
        ]
            .map(stripHtml)
            .filter((part) => part.length > 0)
            .join('. ');
    });

    readonly dynamicClasses = computed(() => {
        if (this.simpleTip()) {
            return 'p-16';
        } else {
            return this.proTip()?.actionCallbacks.length
                ? 'pl-16 pt-16 pr-12 pb-12'
                : 'py-16 pl-16 pr-12';
        }
    });

    private translate(
        key: string,
        params: Record<string, unknown> | undefined,
    ): string {
        if (!key) return '';
        return this.translateService.instant(key, params) as string;
    }

    readonly leave = leaveWith('animate-slide-out', () => this.onLeaveDone());

    onLeaveDone(): void {
        this.isLeaving = false;
        if (this.pendingTip != null) {
            // a tip was waiting for this one to leave: show it now, reusing
            // the overlay instead of disposing it.
            const next = this.pendingTip;
            this.pendingTip = null;
            this.renderedTip.set(next);
            this.attachTip();
        } else {
            this.overlayRef?.dispose();
            this.overlayRef = null;
        }
    }

    private syncRenderedTip(tip: TipData | null): void {
        if (tip == null) {
            // close requested: let the current tip leave, drop any pending one.
            this.pendingTip = null;
            if (this.renderedTip() != null) {
                this.renderedTip.set(null);
                this.isLeaving = true;
            }
        } else if (this.isLeaving) {
            // a tip is still animating out: queue this one until it's gone.
            this.pendingTip = tip;
        } else {
            // nothing leaving: show immediately (fresh tip or a direct replace,
            // which keeps the same rendered node and just swaps its content).
            this.renderedTip.set(tip);
            this.attachTip();
        }
    }

    close(callback?: () => void): void {
        this.store.dispatch(closeCurrentTip());
        if (callback) {
            callback();
        }
    }

    reload(): void {
        window.location.reload();
    }

    actionClicked(actionCallback: TipActionCallback<A>): void {
        this.close();
        actionCallback.callback(actionCallback.action);
    }

    ngOnDestroy(): void {
        this.overlayRef?.dispose();
    }

    private clearAnnouncementRegions(): void {
        this.document
            .querySelectorAll(`[${ANNOUNCEMENT_ATTRIBUTE}]`)
            .forEach((region) =>
                this.renderer.setProperty(region, 'textContent', ''),
            );
    }

    private writeAnnouncement(text: string): void {
        const modal = this.modalSurfaceService.topModal();
        const region =
            modal == null
                ? this.rootAnnouncementRegion()?.nativeElement
                : this.modalAnnouncementRegion(modal);
        if (region != null) {
            this.renderer.setProperty(region, 'textContent', text);
        }
    }

    private modalAnnouncementRegion(modal: Element): Element | null {
        const existing = modal.querySelector(
            `:scope > [${ANNOUNCEMENT_ATTRIBUTE}]`,
        );
        if (existing != null) {
            return existing;
        }

        const root = this.rootAnnouncementRegion()?.nativeElement;
        if (root == null) {
            return null;
        }

        const region = root.cloneNode(false) as Element;
        this.renderer.appendChild(modal, region);
        return region;
    }

    private attachTip(): void {
        if (this.overlayRef?.hasAttached()) {
            return;
        }

        this.overlayRef = this.overlay.create({
            // panelClass 'w-352' provides 22rem width for CDK's centering calculation.
            positionStrategy: this.overlay
                .position()
                .global()
                .centerHorizontally()
                .bottom('3rem'),
            panelClass: ['w-352', 'xl:w-440'],
        });
        this.overlayRef.attach(new TemplatePortal(this.tipContent(), this.vcr));
    }
}
